// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentDrawer.test.tsx
//
// One comment at a time in the left column (Figma November 2026, frame 0c
// "Comment drawer"): All comments goes back to the list, and N of M with
// previous and next steps through the list in the order it is drawn.
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Button, IconButton, Text } from "@radix-ui/themes";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { type CommentWithOptionalTags } from "./reviewCommentTags";
import { type ReviewListTab } from "./useReviewListView";

export type DrawerSteps = {
  comment: CommentWithOptionalTags;
  position: number;
  total: number;
  previousId?: string;
  nextId?: string;
};

/**
 * Where the picked comment sits among the list's live comments, or null when
 * it is not one of them (deleted, filtered out, or nothing picked).
 *
 * why this order: steps follow what she sees. A flat list steps as drawn;
 * sections step in their own order (To decide, then Approved, then Rejected),
 * and by pin number inside each, since pins run group by group as drawn.
 */
export function drawerSteps({
  sections,
  flat,
  commentNumbers,
  selectedId,
}: {
  /** The list's comments in the sections it draws; one run when flat. */
  sections: readonly (readonly CommentWithOptionalTags[])[];
  flat: boolean;
  commentNumbers: Readonly<Record<string, number>>;
  selectedId: string | null;
}): DrawerSteps | null {
  // Stryker disable next-line ConditionalExpression: the false replacement falls through to findIndex, which matches no comment because every _id is a string, so it still returns null; the true replacement is killed by the drawerSteps step tests.
  if (selectedId == null) return null;
  const byNumber = (a: CommentWithOptionalTags, b: CommentWithOptionalTags) =>
    // why: an unnumbered comment goes last in its section, never NaN the sort.
    (commentNumbers[a._id] ?? Infinity) - (commentNumbers[b._id] ?? Infinity);
  const ordered = sections.flatMap((items) => {
    const live = items.filter((comment) => !comment.isDeleted);
    return flat ? live : [...live].sort(byNumber);
  });
  const index = ordered.findIndex((comment) => comment._id === selectedId);
  if (index < 0) return null;
  return {
    comment: ordered[index],
    position: index + 1,
    total: ordered.length,
    previousId: index > 0 ? ordered[index - 1]._id : undefined,
    nextId: index < ordered.length - 1 ? ordered[index + 1]._id : undefined,
  };
}

export type AskV2ReviewCommentDrawerProps = {
  steps: DrawerSteps;
  onStep: (commentId: string) => void;
  onBack: () => void;
  /** The tab she is stepping through, named beside the position; absent without the tabs. */
  tab?: ReviewListTab;
  /** The comment's open card. */
  children: ReactNode;
};

export function AskV2ReviewCommentDrawer({
  steps,
  onStep,
  onBack,
  tab,
  children,
}: AskV2ReviewCommentDrawerProps) {
  const { t } = useTranslation("ui");
  const { comment, position, total, previousId, nextId } = steps;
  return (
    <section
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid="ask-v2-review-comment-drawer"
      data-comment-id={comment._id}
      aria-label={t("askV2.review.drawer.label")}
    >
      <nav
        className="flex shrink-0 items-center gap-2 px-3 py-2"
        data-testid="ask-v2-review-drawer-nav"
        aria-label={t("reviewArtwork.commentNavigation")}
      >
        <Button
          variant="ghost"
          color="gray"
          size="1"
          data-testid="ask-v2-review-drawer-back"
          onClick={onBack}
        >
          <CaretLeft size={14} weight="bold" aria-hidden="true" />
          {t("askV2.review.drawer.allComments")}
        </Button>
        <Text
          as="span"
          size="1"
          className="ml-auto tabular-nums text-puntt-neutral-gray-11"
          data-testid="ask-v2-review-drawer-position"
        >
          {/* why: the drawer hides the tabs, so this names the set she steps through */}
          {tab != null ? (
            <span data-testid="ask-v2-review-drawer-tab">
              {t(`askV2.review.drawer.tab.${tab}`)} ·{" "}
            </span>
          ) : null}
          {t("askV2.review.drawer.position", { current: position, total })}
        </Text>
        <IconButton
          variant="ghost"
          color="gray"
          size="1"
          data-testid="ask-v2-review-drawer-previous"
          aria-label={t("askV2.review.drawer.previous")}
          disabled={previousId == null}
          onClick={() => {
            // Stryker disable next-line ConditionalExpression: the true replacement calls onStep(previousId), but the button is disabled whenever previousId is null so no click reaches the handler; the false replacement is killed by "steps to the neighbours, skipping a deleted comment, and goes back to the list" in AskV2ReviewCommentsList.test.tsx.
            if (previousId != null) onStep(previousId);
          }}
        >
          <CaretLeft size={14} aria-hidden="true" />
        </IconButton>
        <IconButton
          variant="ghost"
          color="gray"
          size="1"
          data-testid="ask-v2-review-drawer-next"
          aria-label={t("askV2.review.drawer.next")}
          disabled={nextId == null}
          onClick={() => {
            // Stryker disable next-line ConditionalExpression: the true replacement calls onStep(nextId), but the button is disabled whenever nextId is null so no click reaches the handler; the false replacement is killed by "steps to the neighbours, skipping a deleted comment, and goes back to the list" in AskV2ReviewCommentsList.test.tsx.
            if (nextId != null) onStep(nextId);
          }}
        >
          <CaretRight size={14} aria-hidden="true" />
        </IconButton>
      </nav>
      <ul
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
        data-testid="ask-v2-review-drawer-body"
        // why: a memory chip's card keeps inside this column, as in the list (`popoverBoundary`).
        data-popover-boundary
      >
        {children}
      </ul>
    </section>
  );
}
